/* eslint-disable react-hooks/exhaustive-deps */
import { createStyles, FormControl, FormControlLabel, IconButton, makeStyles, Radio, RadioGroup, Theme } from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import { ChartCommonProps, ChartCommonState, IconButtonWrapper, UpIcon, DownIcon } from '../Common';
import { ChartFilter } from '../ChartComponents';
import { SearchCriteria } from '../SearchCriteria';
import CloseIcon from '@material-ui/icons/Close';
import { convertStringNumberToNumber, formatNumber, getLabels, getMaxTickLimit, getSuggestedMaxTick } from '../../helpers/ChartHelper';
import gql from 'graphql-tag';
import { useQuery } from 'react-apollo';
import { DrillDownType } from '../DrillDownType';
import { SelectedItemProps } from '../MattersByStatusChart';
import { Bar, getElementAtEvent } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, Scale, Tick, Chart, ChartEvent, ActiveElement } from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import { ChartInfo } from '../../components/ChartInfo';

import { RV_CHART_CLASS, RV_CHART_COLORS, isChartDataEmpty, rvBarDatasetStyle, rvChartAnimation, rvCategoryAxisStyle, rvTooltipStyle, rvValueAxisStyle } from '../chartTheme';
import { ChartHeading } from '../ChartHeading';
import { ChartLimitNote } from '../ChartLimitNote';
import { AnimatedNumber } from '../AnimatedNumber';
import { ChartSkeleton } from '../../components/loading/ChartSkeleton';
import { ChartEmptyState } from '../ChartEmptyState';
import { ChartNotes } from '../ChartNotes';
import MonetizationOnOutlinedIcon from '@material-ui/icons/MonetizationOnOutlined';
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, zoomPlugin);

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            position: 'relative',
            paddingBottom: '10px',
            flex: 1,
            display: 'flex',
            flexFlow: 'column',
            maxWidth: '100%',
            maxHeight: '100%',
            height: '100%',
            textAlign: 'center',
            '@media print': {
                display: 'flex',
                flexFlow: 'column',
                alignItems: 'center',
                boxShadow: 'none !important',
            },
        },
        popover: {},
        paper: {},
        radioGroup: {
            flexFlow: 'row wrap',
            justifyContent: 'center',
        },
        closeIcon: {
            color: 'rgb(0, 120, 212)',
        },
        titleContainer: {
            display: 'flex',
            flexFlow: 'column',
            padding: '20px 15px',
            '& .title': {
                fontSize: 16,
            },
            '& .colWrapper': {
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                marginTop: 15,
                flexFlow: 'row wrap',
            },
            '& .col': {
                fontSize: '16px',
                fontWeight: 600,
                textAlign: 'center',
                border: '1px solid',
                padding: '15px',
                display: 'flex',
                margin: '0 10px 10px 10px',
                flexFlow: 'column',
                flex: 1,
                '& .value': {
                    fontSize: '36px',
                    padding: '20px 0',
                },
                '& .sub': {
                    display: 'flex',
                    flexFlow: 'row wrap',
                    justifyContent: 'center',
                    alignItems: 'center',
                },
                '& .status': {
                    marginLeft: '10px',
                    display: 'flex',
                    flexFlow: 'row wrap',
                    alignItems: 'center',
                },
            },
            '& .increase': {
                color: 'green',
            },
            '& .decrease': {
                color: 'red',
            },
        },
        barContainer: {
            padding: '5px 15px 15px 15px',
            minHeight: '300px',
            flex: 1,
            display: 'flex',
            flexFlow: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            maxHeight: '350px',
            maxWidth: 'auto',
            position: 'relative',
            '@media (max-width: 533px)': {
                maxWidth: '350px',
                margin: '0 auto',
            },
            '& .titleWrapper': {
                textAlign: 'center',
                display: 'inline-block',
                width: '100%',
                padding: '15px',
                '& .title': {
                    fontSize: '18px',
                    fontWeight: 600,
                    display: 'block',
                    marginBottom: '15px',
                },
                '& .value': {
                    fontSize: '24px',
                    fontWeight: 600,
                    display: 'block',
                    marginBottom: '15px',
                },
                '& .secondary-title': {
                    fontSize: '16px',
                    fontWeight: 400,
                    display: 'block',
                    marginBottom: '15px',
                },
            },
        },
        loadingWrapper: {
            padding: '20px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
        },
        title: {
            fontSize: 16,
            fontWeight: 600,
            fontFamily: 'inherit',
            marginBottom: 10,
        },
        totalDiv: {
            cursor: 'pointer',
            paddingRight: 5,
        },
        span: {
            fontSize: '1rem',
        },
        helpIcon: {
            position: 'absolute',
            left: '5px',
            top: '5px',
            '@media print': {
                display: 'none !important',
            },
        },
    }),
);

export enum LastCountEnum {
    PREV_MONTH,
    MONTH,
    FINANCIAL_YEAR,
    PREV_FINANCIAL_YEAR,
}

interface CollectionRateChartProps extends SelectedItemProps, ChartCommonProps {
    chartFilter?: ChartFilter;
}

interface ChartState extends ChartCommonState {
    last: LastCountEnum;
    nameData: string[];
    collectionRateData: number[];
    feesBilledData: number[];
    feesCollectedData: number[];

    currentMonthtTotalCollectionRate: number;
    currentYeartTotalCollectionRate: number;
    previousMonthtTotalCollectionRate: number;
    previousYearTotalCollectionRate: number;
    symbol: string;
}

export const CollectionRateChart: React.FC<CollectionRateChartProps> = (props) => {
    const classes = useStyles();

    // tslint:disable-next-line:no-any
    const chartRef = React.useRef<any>();

    // tslint:disable-next-line:no-any
    const { loading, error, data } = useQuery<CollectionRateData>(COLLECTION_RATE_QUERY, {
        variables: {
            entityType: props.chartFilter?.entityType,
            entityGuid: props.chartFilter?.entityGuid,
            responsibility: props.chartFilter?.responsibility,
        },
    });

    const [state, setState] = useState<ChartState>({
        open: false,
        anchorEl: undefined,
        last: LastCountEnum.FINANCIAL_YEAR,
        nameData: [],
        collectionRateData: [],
        feesBilledData: [],
        feesCollectedData: [],

        currentMonthtTotalCollectionRate: 0,
        currentYeartTotalCollectionRate: 0,
        previousMonthtTotalCollectionRate: 0,
        previousYearTotalCollectionRate: 0,
        symbol: '$',
    });

    const currencySymbolQuery = useQuery<CurrencySymbolData>(CURRENCY_SYMBOL_QUERY);

    useEffect(() => {
        if (currencySymbolQuery.data && currencySymbolQuery.data.options && !currencySymbolQuery.loading && !currencySymbolQuery.error) {
            setState((prevState) => {
                return {
                    ...prevState,
                    symbol: currencySymbolQuery.data!.options.currencySymbol.symbol,
                };
            });
        }
        // tslint:disable-next-line: align
    }, [currencySymbolQuery.loading, currencySymbolQuery.error, currencySymbolQuery.data]);

    useEffect(() => {
        if (!loading) {
            setState((prevState) => {
                let nameData: string[] = [];
                let collectionRateData: number[] = [];
                let feesBilledData: number[] = [];
                let feesCollectedData: number[] = [];

                let currentMonthtTotalCollectionRate: number = 0;
                let currentYeartTotalCollectionRate: number = 0;
                let previousMonthtTotalCollectionRate: number = 0;
                let previousYearTotalCollectionRate: number = 0;

                let periodString: string = '';
                if (!error && data && data.statistics && data.statistics.fees) {
                    var previousMonth = data.statistics.fees.collectionRate.previousMonth;
                    var currentFinancialYear = data.statistics.fees.collectionRate.currentFinancialYear;
                    var currentMonth = data.statistics.fees.collectionRate.currentMonth;
                    var previousFinancialYear = data.statistics.fees.collectionRate.previousFinancialYear;

                    currentYeartTotalCollectionRate = currentFinancialYear.collectionRateTotal.totalCollectionRate * 100;
                    currentMonthtTotalCollectionRate = currentMonth.collectionRateTotal.totalCollectionRate * 100;
                    previousMonthtTotalCollectionRate = previousMonth.collectionRateTotal.totalCollectionRate * 100;
                    previousYearTotalCollectionRate = previousFinancialYear.collectionRateTotal.totalCollectionRate * 100;

                    if (state.last === LastCountEnum.MONTH) {
                        periodString = 'Month';
                        nameData = currentMonth.collectionRateByStaff.map((bill: CollectionRateByStaff) => (bill.name ? bill.name : ''));
                        collectionRateData = currentMonth.collectionRateByStaff.map((bill: CollectionRateByStaff) => bill.collectionRate * 100);
                        feesBilledData = currentMonth.collectionRateByStaff.map((bill: CollectionRateByStaff) => bill.feesBilled);
                        feesCollectedData = currentMonth.collectionRateByStaff.map((bill: CollectionRateByStaff) => bill.feesCollected);
                    } else if (state.last === LastCountEnum.FINANCIAL_YEAR) {
                        periodString = 'Financial Year';
                        nameData = currentFinancialYear.collectionRateByStaff.map((bill: CollectionRateByStaff) => (bill.name ? bill.name : ''));
                        collectionRateData = currentFinancialYear.collectionRateByStaff.map((bill: CollectionRateByStaff) => bill.collectionRate * 100);
                        feesBilledData = currentFinancialYear.collectionRateByStaff.map((bill: CollectionRateByStaff) => bill.feesBilled);
                        feesCollectedData = currentFinancialYear.collectionRateByStaff.map((bill: CollectionRateByStaff) => bill.feesCollected);
                    } else if (state.last === LastCountEnum.PREV_MONTH) {
                        periodString = 'Previous Month';
                        nameData = previousMonth.collectionRateByStaff.map((bill: CollectionRateByStaff) => (bill.name ? bill.name : ''));
                        collectionRateData = previousMonth.collectionRateByStaff.map((bill: CollectionRateByStaff) => bill.collectionRate * 100);
                        feesBilledData = previousMonth.collectionRateByStaff.map((bill: CollectionRateByStaff) => bill.feesBilled);
                        feesCollectedData = previousMonth.collectionRateByStaff.map((bill: CollectionRateByStaff) => bill.feesCollected);
                        // currentYeartTotalCollectionRate = previousFinancialYear.billingRealisationTotal.TotalCollectionRate * 100;
                        // currentMonthtTotalCollectionRate = previousMonth.billingRealisationTotal.TotalCollectionRate * 100;
                    } else {
                        periodString = 'Previous Financial Year';
                        nameData = previousFinancialYear.collectionRateByStaff.map((bill: CollectionRateByStaff) => (bill.name ? bill.name : ''));
                        collectionRateData = previousFinancialYear.collectionRateByStaff.map((bill: CollectionRateByStaff) => bill.collectionRate * 100);
                        feesBilledData = previousFinancialYear.collectionRateByStaff.map((bill: CollectionRateByStaff) => bill.feesBilled);
                        feesCollectedData = previousFinancialYear.collectionRateByStaff.map((bill: CollectionRateByStaff) => bill.feesCollected);
                        // currentYeartTotalCollectionRate = previousFinancialYear.billingRealisationTotal.TotalCollectionRate * 100;
                        // currentMonthtTotalCollectionRate = previousMonth.billingRealisationTotal.TotalCollectionRate * 100;
                    }
                }

                return {
                    ...prevState,
                    periodString,
                    nameData,
                    collectionRateData,
                    feesBilledData,
                    feesCollectedData,
                    currentMonthtTotalCollectionRate,
                    currentYeartTotalCollectionRate,
                    previousMonthtTotalCollectionRate,
                    previousYearTotalCollectionRate,
                };
            });
        }

        // tslint:disable-next-line: align
    }, [state.last, loading, error, data]);

    const onCloseChart = () => {
        if (props.onCloseChart) {
            props.onCloseChart('isCollectionRateChartOpen');
        }
    };

    const onChangeRadio = (event: React.ChangeEvent<HTMLInputElement>) => {
        setState((prevState) => {
            return {
                ...prevState,
                // tslint:disable-next-line: radix
                last: parseInt((event.target as HTMLInputElement).value),
            };
        });
    };

    const handleTotalClick = (isFinancialYear: Boolean) => {
        if (props.getSelectedItem) {
            const searchCriterias = new Array<SearchCriteria>();

            if (isFinancialYear) {
                if (state.last === LastCountEnum.MONTH || state.last === LastCountEnum.FINANCIAL_YEAR) {
                    searchCriterias.push({ property: 'FinancialYear', operator: 'eq', comparison: 'curr' });
                } else {
                    searchCriterias.push({ property: 'FinancialYear', operator: 'eq', comparison: 'prev' });
                }
            } else {
                if (state.last === LastCountEnum.MONTH || state.last === LastCountEnum.FINANCIAL_YEAR) {
                    searchCriterias.push({ property: 'Month', operator: 'eq', comparison: 'curr' });
                } else {
                    searchCriterias.push({ property: 'Month', operator: 'eq', comparison: 'prev' });
                }
            }

            props.getSelectedItem(searchCriterias, false, 'Collection Rate', DrillDownType.staffBillingSummary, 'Collections');
        }
    };

    const onPan = (context: { chart: Chart }) => {
        chartRef.current.options.scales.x.min = 0;
        chartRef.current.update();
    };

    const onHover = (event: ChartEvent, elements: ActiveElement[], chart: Chart) => {
        (event?.native?.target as HTMLElement).style.cursor = elements.length > 0 ? 'pointer' : 'auto';
    };

    if (loading || currencySymbolQuery.loading) {
        return (
            <ChartSkeleton variant="bars" />
        );
    }
    return (
        <div className={`${classes.root} ${RV_CHART_CLASS}${isChartDataEmpty(state.collectionRateData) ? ' rv-chart--empty' : ''}`}>
            <IconButtonWrapper aria-label="close">
                <ChartInfo 
                    description={props.description}
                    fontSize={props.showAllRecords ? 'large' : 'medium'}
                    className={`${classes.helpIcon} rv-chart-help`}
                />
                <IconButton aria-label="close" onClick={onCloseChart} size="small">
                    <CloseIcon className={classes.closeIcon} />
                </IconButton>
            </IconButtonWrapper>
            <div className={classes.titleContainer}>
                <ChartHeading icon={<MonetizationOnOutlinedIcon />} title="Collection Rate" />
                <div className="colWrapper">
                    <div className="col">
                        <div className="title">Month to Date</div>
                        <div className={`${classes.totalDiv} value`} onClick={() => handleTotalClick(false)}>
                            <AnimatedNumber value={state.currentMonthtTotalCollectionRate} format={(amount) => formatNumber(amount, 1)} />%
                            {
                                // tslint:disable-next-line: max-line-length
                                state.currentMonthtTotalCollectionRate > state.previousMonthtTotalCollectionRate ? (
                                    <UpIcon className="increase" />
                                ) : state.currentMonthtTotalCollectionRate < state.previousMonthtTotalCollectionRate ? (
                                    <DownIcon className="decrease" />
                                ) : (
                                    <div />
                                )
                            }
                        </div>
                        <div className="sub">{`Previous ${formatNumber(state.previousMonthtTotalCollectionRate, 1)}%`}</div>
                    </div>
                    <div className="col">
                        <div className="title">Year to Date</div>
                        <div className={`${classes.totalDiv} value`} onClick={() => handleTotalClick(true)}>
                            <AnimatedNumber value={state.currentYeartTotalCollectionRate} format={(amount) => formatNumber(amount, 1)} />%
                            {state.currentYeartTotalCollectionRate > state.previousYearTotalCollectionRate ? (
                                <UpIcon className="increase" />
                            ) : state.currentYeartTotalCollectionRate < state.previousYearTotalCollectionRate ? (
                                <DownIcon className="decrease" />
                            ) : (
                                <div />
                            )}
                        </div>
                        <div className="sub">{`Previous ${formatNumber(state.previousYearTotalCollectionRate, 1)}%`}</div>
                    </div>
                </div>
            </div>
            <div className={classes.barContainer}>
                <Bar
                    ref={chartRef}
                    // tslint:disable-next-line: no-any
                    onClick={(event: React.MouseEvent<any>) => {
                        const _data = getElementAtEvent(chartRef.current, event);
                        if (_data.length > 0 && props.getSelectedItem) {
                            const _index = getElementAtEvent(chartRef.current, event)[0].index;

                            const item = state.nameData[_index];

                            const searchCriterias = new Array<SearchCriteria>();
                            // var d = new Date();
                            // var year = d.getFullYear();

                            searchCriterias.push(
                                { property: 'StaffName', operator: 'eq', comparison: item }, //
                            );

                            if (state.last === LastCountEnum.MONTH) {
                                searchCriterias.push({ property: 'Month', operator: 'eq', comparison: '', period: 'curr' });
                            } else if (state.last === LastCountEnum.PREV_MONTH) {
                                searchCriterias.push({ property: 'Month', operator: 'eq', comparison: '', period: 'prev' });
                            } else if (state.last === LastCountEnum.FINANCIAL_YEAR) {
                                searchCriterias.push({ property: 'FinancialYear', operator: 'eq', comparison: '', period: 'curr' });
                            } else if (state.last === LastCountEnum.PREV_FINANCIAL_YEAR) {
                                searchCriterias.push({ property: 'FinancialYear', operator: 'eq', comparison: '', period: 'prev'  });
                            }

                            props.getSelectedItem(searchCriterias, false, 'Collection Rate - ' + item, DrillDownType.staffBillingSummary, 'Collections');
                        }
                    }}
                    data={{
                        labels: getLabels(state.nameData),
                        datasets: [
                            {
                                label: 'Collection Rate',
                                ...rvBarDatasetStyle(RV_CHART_COLORS.primary, true),
                                data: state.collectionRateData,
                            },
                        ],
                    }}
                    options={{
                        onHover: onHover,
                        indexAxis: 'y' as const,
                        animation: rvChartAnimation(),
                        elements: { bar: { borderWidth: 0 } },
                        responsive: true,
                        maintainAspectRatio: false,
                        scales: {
                            x: {
                                grid: rvValueAxisStyle.grid,
                                // beginAtZero: true,
                                stacked: true,
                                suggestedMax: getSuggestedMaxTick(state.collectionRateData),
                                beginAtZero: true,
                                ticks: {
                                    ...rvValueAxisStyle.ticks,
                                    maxTicksLimit: getMaxTickLimit(state.collectionRateData),
                                    autoSkip: false,
                                    callback: function (this: Scale, tickValue: number, index: number, ticks: Tick[]) {
                                        return `${formatNumber(tickValue)}%`;
                                    },
                                },
                            },
                            y: {
                                grid: rvCategoryAxisStyle.grid,
                                stacked: true,
                                ticks: {
                                    ...rvCategoryAxisStyle.ticks,
                                    autoSkip: false,
                                },
                                // min: 0,
                                // max: 9 // minimum display
                            },
                        },
                        plugins: {
                            legend: {
                                position: 'bottom' as const,
                                display: false,
                            },
                            title: {
                                display: false,
                                text: ``,
                            },
                            zoom: {
                                pan: {
                                    enabled: false,
                                    onPan: onPan,
                                },
                                zoom: {
                                    wheel: {
                                        enabled: false,
                                    },
                                },
                                // limits: {
                                //   y: { min: 0, max: 9 },
                                // },
                            },
                            tooltip: {
                                ...rvTooltipStyle,
                                callbacks: {
                                    // tslint:disable-next-line: no-any
                                    label: function (context: any) {
                                        const {
                                            //   dataIndex,
                                            //   label,
                                            formattedValue,
                                            dataset,
                                        } = context;

                                        var currentValue = convertStringNumberToNumber(formattedValue);

                                        return `${dataset.label}: ${formatNumber(currentValue, 1)}%`;
                                    },
                                    // tslint:disable-next-line: no-any no-shadowed-variable
                                    afterLabel: function (context: any) {
                                        const {
                                            dataIndex,
                                            //   label,
                                            // formattedValue,
                                            // dataset,
                                        } = context;

                                        return [
                                            `Fees Billed: ${state.symbol}${formatNumber(state.feesBilledData[dataIndex])}`,
                                            `Fees Collected: ${state.symbol}${formatNumber(state.feesCollectedData[dataIndex])}`,
                                        ];
                                    },
                                },
                            },
                        },
                    }}
                />
                {isChartDataEmpty(state.collectionRateData) && (
                    <ChartEmptyState title="No data" hint="Nothing to show for this selection." />
                )}
            </div>
            <FormControl component="div">
                <RadioGroup name="last" value={state.last} onChange={onChangeRadio} className={classes.radioGroup}>
                    <FormControlLabel value={LastCountEnum.FINANCIAL_YEAR} control={<Radio color="primary" />} label="Financial Year" />
                    <FormControlLabel value={LastCountEnum.PREV_FINANCIAL_YEAR} control={<Radio color="primary" />} label="Previous Financial Year" />
                    <FormControlLabel value={LastCountEnum.MONTH} control={<Radio color="primary" />} label="This Month" />
                    <FormControlLabel value={LastCountEnum.PREV_MONTH} control={<Radio color="primary" />} label="Previous Month" />
                </RadioGroup>
            </FormControl>
            <ChartNotes notes={['Information collated based on Timekeeper']} actionHint="Drill down to timekeeper details" />
            <ChartLimitNote>The top 10 timekeepers are shown</ChartLimitNote>
        </div>
    );
};

export interface CurrencySymbolData {
    options: Options;
}

export interface Options {
    currencySymbol: CurrencySymbol;
}

export interface CurrencySymbol {
    symbol: string;
}

export const CURRENCY_SYMBOL_QUERY = gql`
    query CurrencySymbol {
        options {
            currencySymbol {
                symbol
            }
        }
    }
`;

export interface CollectionRateData {
    statistics: Statistics;
}

export interface Statistics {
    fees: Fees;
}

export interface Fees {
    collectionRate: CollectionRate;
}

export interface CollectionRate {
    currentFinancialYear: CurrentFinancialYear;
    previousFinancialYear: CurrentFinancialYear;
    currentMonth: CurrentFinancialYear;
    previousMonth: CurrentFinancialYear;
}

export interface CurrentFinancialYear {
    collectionRateByStaff: CollectionRateByStaff[];
    collectionRateTotal: CollectionRateTotal;
}

export interface CollectionRateByStaff {
    feesBilled: number;
    feesCollected: number;
    name: string;
    collectionRate: number;
}

export interface CollectionRateTotal {
    totalFeesBilled: number;
    totalFeesCollected: number;
    totalCollectionRate: number;
}

const COLLECTION_RATE_QUERY = gql`
    # Write your query or mutation here
    query collectionRate($entityType: Int, $entityGuid: String, $responsibility: Int, $include: Boolean) {
        statistics {
            fees {
                collectionRate(entityType: $entityType, entityGuid: $entityGuid, responsibility: $responsibility, include: $include) {
                    currentFinancialYear {
                        collectionRateByStaff {
                            feesBilled
                            feesCollected
                            name
                            collectionRate
                        }
                        collectionRateTotal {
                            totalFeesBilled
                            totalFeesCollected
                            totalCollectionRate
                        }
                    }
                    previousFinancialYear {
                        collectionRateByStaff {
                            feesBilled
                            feesCollected
                            name
                            collectionRate
                        }
                        collectionRateTotal {
                            totalFeesBilled
                            totalFeesCollected
                            totalCollectionRate
                        }
                    }
                    currentMonth {
                        collectionRateByStaff {
                            feesBilled
                            feesCollected
                            name
                            collectionRate
                        }
                        collectionRateTotal {
                            totalFeesBilled
                            totalFeesCollected
                            totalCollectionRate
                        }
                    }
                    previousMonth {
                        collectionRateByStaff {
                            feesBilled
                            feesCollected
                            name
                            collectionRate
                        }
                        collectionRateTotal {
                            totalFeesBilled
                            totalFeesCollected
                            totalCollectionRate
                        }
                    }
                }
            }
        }
    }
`;
